import { ReactElement } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import { Add, Delete } from '@mui/icons-material'
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd'
import type { DropResult } from '@hello-pangea/dnd'
import { useNavigate } from 'react-router-dom'
import { DragAndDropIcon, EditIcon, RemoveIcon } from 'src/v3/core/icons'
import { LightweightCourseV5 } from '../../course/types'
import { CREATE_COURSE_URLS } from '../../constants'

export type SeasonState = {
  seasonNumber: number
  courseIDs: string[]
}

type SeasonSectionProps = {
  season: SeasonState
  allCourses: LightweightCourseV5[]
  isSaving: boolean
  onAddCourses: () => void
  onRemoveCourse: (courseID: string) => void
  onReorder: (newCourseIDs: string[]) => void
  onDeleteSeason: () => void
}

export default function SeasonSection({
  season,
  allCourses,
  isSaving,
  onAddCourses,
  onRemoveCourse,
  onReorder,
  onDeleteSeason,
}: SeasonSectionProps): ReactElement {
  let navigate = useNavigate()

  let handleDragEnd = (result: DropResult) => {
    if (!result.destination) return
    let src = result.source.index
    let dst = result.destination.index
    let moved = season.courseIDs[src]
    let reordered = season.courseIDs
      .filter((_, i) => i !== src)
      .toSpliced(dst, 0, moved)
    onReorder(reordered)
  }

  let courseMap = new Map(allCourses.map((c) => [c.id, c]))

  return (
    <Card
      className="mb-4"
      variant="outlined"
      sx={{ bgcolor: 'background.paper', borderColor: 'divider' }}
    >
      <CardContent>
        <Box className="flex items-center gap-2 mb-3">
          <Typography variant="body1" component="h3" className="flex-1">
            Season {season.seasonNumber}
          </Typography>
          <Button
            size="large"
            variant="outlined"
            startIcon={<Add />}
            onClick={onAddCourses}
            disabled={isSaving}
          >
            Add Episodes
          </Button>
          <Tooltip
            title={
              season.courseIDs.length > 0
                ? 'Remove all courses before deleting this season'
                : 'Delete season'
            }
          >
            <span>
              <IconButton
                size="small"
                color="error"
                onClick={onDeleteSeason}
                disabled={isSaving || season.courseIDs.length > 0}
                aria-label={`Delete Season ${season.seasonNumber}`}
              >
                <Delete fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {season.courseIDs.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary' }}
            className="py-4 text-center"
          >
            No courses yet. Click Add Courses to get started.
          </Typography>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId={`season-${season.seasonNumber}`}>
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {season.courseIDs.map((courseID, index) => {
                    let course = courseMap.get(courseID)
                    return (
                      <Draggable
                        key={courseID}
                        draggableId={courseID}
                        index={index}
                        isDragDisabled={isSaving}
                      >
                        {(prov, snapshot) => (
                          <Card
                            ref={prov.innerRef}
                            {...prov.draggableProps}
                            variant="outlined"
                            className="flex items-center gap-3 p-3 mb-2 cursor-grab active:cursor-grabbing"
                            sx={{
                              bgcolor: 'background.paperDark',
                              opacity:
                                snapshot.isDragging || isSaving ? 0.5 : 1,
                            }}
                          >
                            <Box
                              {...prov.dragHandleProps}
                              className="flex items-center"
                              sx={{ color: 'text.disabled' }}
                              aria-label={`Drag to reorder ${course?.name ?? courseID}`}
                            >
                              <DragAndDropIcon aria-hidden="true" />
                            </Box>
                            <Chip
                              label={`Ep ${index + 1}`}
                              size="medium"
                              variant="outlined"
                              sx={{ flexShrink: 0 }}
                            />
                            <Box className="flex-1 min-w-0">
                              <Typography variant="body2" className="truncate">
                                {course?.name ?? courseID}
                              </Typography>
                            </Box>
                            {course && (
                              <>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: 'text.secondary',
                                    flexShrink: 0,
                                  }}
                                >
                                  {course.length} hr
                                </Typography>
                                <Chip
                                  label={
                                    course.published === 'published'
                                      ? 'Published'
                                      : 'Draft'
                                  }
                                  color={
                                    course.published === 'published'
                                      ? 'success'
                                      : 'warning'
                                  }
                                  size="medium"
                                  sx={{ flexShrink: 0 }}
                                />
                              </>
                            )}
                            <Tooltip title="Edit course">
                              <IconButton
                                size="small"
                                color="primary"
                                aria-label={`Edit ${course?.name ?? courseID}`}
                                onClick={() =>
                                  navigate(CREATE_COURSE_URLS.EDIT(courseID))
                                }
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remove from season">
                              <IconButton
                                size="small"
                                color="error"
                                aria-label={`Remove ${course?.name ?? courseID} from season`}
                                onClick={() => onRemoveCourse(courseID)}
                                disabled={isSaving}
                              >
                                <RemoveIcon />
                              </IconButton>
                            </Tooltip>
                          </Card>
                        )}
                      </Draggable>
                    )
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </CardContent>
    </Card>
  )
}
